import {
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { Fragment, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
	useEditReferrerMutation,
	useEditShareMutation,
	useGetAdmissionRequestDetailQuery,
} from '@/api/member-management/admissionRequest';
import { EditReferrerRequest } from '@/api/DTO/member-management/admissionRequest';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';

import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { YESNO, YESNOLIST } from '@/common/constants/common';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: string;
}
export default function EditShareInformation(props: Props) {
	const { type } = props;
	const memberType = i18n.t(
		type === 'admissionRequest'
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId } = useParams();
	const [editShare] = useEditShareMutation();

	const { data: details } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		otherOrganisationList: Yup.array().when('otherOrganisationServing', {
			is: (val: any) => val === 1,
			then: (schema) =>
				schema.of(
					Yup.object().shape({
						clientName: Yup.string().required(t('errorMessage.pleaseEnter')),
						organization: Yup.string().required(t('errorMessage.pleaseEnter')),
						name: Yup.string().required(t('errorMessage.pleaseEnter')),
						telNo: Yup.string()
							.required(t('errorMessage.pleaseEnter'))
							.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
					})
				),
			otherwise: (schema) => schema,
		}),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	const watchOtherOrganisationServing = methods.watch(
		'otherOrganisationServing'
	);

	const {
		fields: shareCaseFields,
		append: shareCaseAppend,
		replace: shareCaseReplace,
		update: shareCaseUpdate,
	}: any = useFieldArray({
		control: methods.control,
		name: 'otherOrganisationList',
	});

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
			if (details.data.organisationList)
				shareCaseReplace(details.data.organisationList);
		}
	}, [details]);

	const onSubmit: SubmitHandler<any> = async (data) => {
		const obj: any = { ...data };
		const response = await editShare(obj);

		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				`/member-management/${
					type == 'member' ? 'member-list' : 'admission-request-list'
				}/detail/${admissionRequestId}`
			);
		}
	};

	const handleClose = () => {
		navigate(
			`/member-management/${
				type == 'member' ? 'member-list' : 'admission-request-list'
			}/detail/${admissionRequestId}`,
			{ replace: true }
		);
	};

	const addShareCase = () => {
		shareCaseAppend({});
	};

	// Removing a row shifts the field name of every row below it. The inputs are
	// controllers bound to those names, so with remove() they keep the value they
	// held under the old index and the last row's values drop out of the form
	// state - the row still shows them, but submitting reports it as empty.
	// Rebuilding the list with replace() hands every row a fresh key, so they
	// remount against the shifted values.
	const removeShareCase = (index: number) => {
		shareCaseReplace(
			(methods.getValues('otherOrganisationList') || []).filter(
				(_: any, i: number) => i !== index
			)
		);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h4'>
								{type == 'admissionRequest'
									? t('drawerListItems.editAdmissionRequest')
									: t('drawerListItems.editMember')}
							</Typography>
						</Box>
						<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
							<Box
								display='flex'
								justifyContent='space-between'
								marginBottom={2}
							>
								<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
									{t('preAdmission.shareCaseService')}
								</Typography>
							</Box>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='otherOrganisationServing'
										label={t('preAdmission.otherOrganisationServingSameCase')}
										options={YESNO}
										handleChange={() => {
											shareCaseReplace([]);
										}}
									/>
								</Grid>
							</Grid>
							{watchOtherOrganisationServing
								? shareCaseFields.map((item: any, index: number) => (
										<Fragment key={item.id}>
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'space-between',
												}}
												marginBottom={1}
											>
												<Typography variant='body1' marginBottom={2}>
													{t('preAdmission.organisation') + ` ${index + 1}`}
												</Typography>
												<IconButton
													onClick={() => {
														removeShareCase(index);
													}}
												>
													<CloseOutlinedIcon />
												</IconButton>
											</Box>
											<Grid
												container
												spacing={3}
												marginTop={0}
												marginBottom={2}
											>
												<Grid size={6}>
													<AppTextField
														control={methods.control}
														name={`otherOrganisationList[${index}].clientName`}
														label={t('preAdmission.clientName') + '*'}
													/>
												</Grid>
												<Grid size={6}>
													<AppTextField
														control={methods.control}
														name={`otherOrganisationList[${index}].organization`}
														label={t('preAdmission.organisation') + '*'}
													/>
												</Grid>
												<Grid size={6}>
													<AppTextField
														control={methods.control}
														name={`otherOrganisationList[${index}].name`}
														label={t('preAdmission.name') + '*'}
													/>
												</Grid>
												<Grid size={6}>
													<AppTextField
														control={methods.control}
														name={`otherOrganisationList[${index}].telNo`}
														label={t('preAdmission.telNo') + '*'}
													/>
												</Grid>
											</Grid>
										</Fragment>
									))
								: ''}
							{watchOtherOrganisationServing
								? shareCaseFields.length < 5 && (
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid size={12}>
												<Button
													startIcon={<AddCircleOutlineOutlinedIcon />}
													size='large'
													onClick={addShareCase}
												>
													{t('button.newOrganisation')}
												</Button>
											</Grid>
										</Grid>
									)
								: ''}
							<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
								<Button onClick={handleClose}>{t('button.cancel')}</Button>
								<Button
									type='submit'
									variant='contained'
									endIcon={<CheckCircleOutlineIcon />}
								>
									{t('button.complete')}
								</Button>
							</CardActions>
						</Card>
					</form>
				</FormProvider>
			</Box>
		</>
	);
}
